import type { ButtonHTMLAttributes } from "react";
import type { Insight } from "../../lib/api";

/**
 * What the door is telling the recipient (FR-7.4). Each insight is a grounded
 * sentence — phrased by the model, mapped to a computed aggregate, never
 * invented — with a one-tap action that accepts or dismisses the House Rules
 * proposal behind it (M15). An insight with no linked proposal is only telling
 * the recipient something, so it stands as a sentence with nothing to answer.
 *
 * Every row here is a pure render of what it is handed; the network state lives
 * up in `RecapArea`, which is what lets the grounded-only rule be asserted.
 */

/** The transient per-insight state: settled once accepted or dismissed. */
export type InsightState = { settled: "accepted" | "dismissed" | null; busy: boolean };

const CLOSED_INSIGHT: InsightState = { settled: null, busy: false };

const ACTION =
  "text-sm text-text-soft transition-colors duration-75 hover:text-text " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-50 " +
  "disabled:pointer-events-none rounded";

function ActionButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return <button type="button" {...rest} className={`${ACTION} ${className}`} />;
}

type SectionProps = {
  insights: Insight[];
  state: Record<string, InsightState>;
  onAccept: (insight: Insight) => void;
  onDismiss: (insight: Insight) => void;
};

/**
 * The quiet section under the five (FR-7.3). Absent entirely when nothing is
 * waiting — a door with nothing to say says nothing. No red dots, no nagging: a
 * heading and the grounded lines.
 */
export function InsightsSection({ insights, state, onAccept, onDismiss }: SectionProps) {
  if (insights.length === 0) return null;

  return (
    <section className="mt-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-faint">
        What your door is telling you
      </p>
      <ul className="mt-5 space-y-5">
        {insights.map((insight, i) => (
          <InsightRow
            key={insight.id || `insight-${i}`}
            insight={insight}
            state={state[insight.id] ?? CLOSED_INSIGHT}
            onAccept={() => onAccept(insight)}
            onDismiss={() => onDismiss(insight)}
          />
        ))}
      </ul>
    </section>
  );
}

function InsightRow({
  insight,
  state,
  onAccept,
  onDismiss,
}: {
  insight: Insight;
  state: InsightState;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const affirm = insight.action_label ?? "Yes";

  return (
    <li
      className="flex flex-col gap-3 border-l-2 border-border pl-4 sm:flex-row sm:items-start
                 sm:justify-between sm:gap-6"
    >
      <p className="text-[15px] leading-relaxed text-text-soft">{insight.text}</p>

      {insight.proposal_id && (
        <div className="flex shrink-0 items-center gap-3 sm:pt-0.5" aria-live="polite">
          {state.settled === "accepted" ? (
            <span className="font-mono text-[11px] text-faint">Added.</span>
          ) : state.settled === "dismissed" ? (
            <span className="font-mono text-[11px] text-faint">Dismissed.</span>
          ) : (
            <>
              <ActionButton
                onClick={onAccept}
                disabled={state.busy}
                className="text-accent-ink-strong/90"
              >
                {affirm}
              </ActionButton>
              <ActionButton onClick={onDismiss} disabled={state.busy}>
                No
              </ActionButton>
            </>
          )}
        </div>
      )}
    </li>
  );
}
